import _ from 'lodash';
import { CALCULATION_CONTENT_CONTAINER_ID } from '../../../shared/constants';
import convertToRange from './convertToRange';
import surroundTextNodesInRange from './surroundTextNodesInRange';
import { ContentArea } from '../types';

export const iconBtnIdExtension = '-icon-btn';
export const linkButtonClass = 'link-button';

interface IContentProps {
  content: string;
  elementsNotFound: string[];
}
interface IMakeContentProps {
  content: string;
  contentAreas: ContentArea[];
  isSuggestionContent?: boolean;
  suggestionElId?: string;
}
interface IGetAccessibleIconBtnProps {
  id: string;
  iconClassName: string;
  description: string;
}

/**
 * Generates a button used for keyboard navigation to marks.
 *
 * @param {string} id - The ID assigned to the button.
 * @param {string} iconClassName - The class name assigned to the button.
 * @param {string} description - The description assigned to the button.
 * @returns {HTMLButtonElement} The button used for the mark.
 */
export const getAccessibleIconBtn = ({
  id,
  iconClassName,
  description,
}: IGetAccessibleIconBtnProps): HTMLButtonElement => {
  const button = document.createElement('button');
  const icon = document.createElement('i');

  button.setAttribute('id', `${id}${iconBtnIdExtension}`);
  button.classList.add(
    'py-0',
    'px-1',
    'border-0',
    'bg-transparent',
    linkButtonClass,
  );
  button.appendChild(icon);
  button.setAttribute('title', description || '');
  button.setAttribute('aria-label', description || '');

  icon.classList.add(iconClassName, 'ref-icon');
  icon.setAttribute('aria-hidden', 'true');

  return button;
};

/**
 * Determines if the range accurately represents the intended content area.
 * Checks if the characters before the range are the same in both cases.
 *
 * @param {Range} range - The range that is supposed to hold the marked text.
 * @param {Document} htmlDoc - The document containing the range.
 * @param {ContentArea} contentArea - The object in the database that is supposed to represent the range.
 * @returns {boolean} - True if the range matches the content area, false otherwise.
 */
const getAdditionalCharsMatch = (
  range: Range,
  htmlDoc: Document,
  contentArea: ContentArea,
) => {
  const contentNode =
    document.getElementById(CALCULATION_CONTENT_CONTAINER_ID) || htmlDoc.body;

  const rangeBeforePattern = range.cloneRange();
  rangeBeforePattern.collapse(true);
  rangeBeforePattern.setStartBefore(contentNode);
  const textBeforePattern = rangeBeforePattern.toString();
  const precedingCharsMatch = textBeforePattern.match(
    new RegExp(`${_.escapeRegExp(contentArea?.precedingCharacters || '')}$`),
  );

  const rangeAfterPattern = range.cloneRange();
  rangeAfterPattern.collapse();
  rangeAfterPattern.setEndAfter(contentNode);
  const textAfterPattern = rangeAfterPattern.toString();
  const subsequentCharsMatch = textAfterPattern.match(
    new RegExp(`^${_.escapeRegExp(contentArea?.subsequentCharacters || '')}`),
  );

  return precedingCharsMatch && subsequentCharsMatch;
};

/**
 * Generates a new HTML content string.
 *
 * @interface IMakeContentProps
 * @property {string} content - The content to which the elements belong.
 * @property {ContentArea[]} contentAreas - The elements to include.
 * @returns {IContentProps} - The properties of the generated content.
 */
const makeContent = ({
  content,
  contentAreas,
}: IMakeContentProps): IContentProps => {
  if (contentAreas.length === 0) {
    return { content, elementsNotFound: [] };
  }

  // Parse content string to html document
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(
    `<div id='${CALCULATION_CONTENT_CONTAINER_ID}'>${content}</div>`,
    'text/html',
  );

  let elementsNotFound: string[] = [];

  contentAreas.forEach((a) => {
    const range = convertToRange(htmlDoc, CALCULATION_CONTENT_CONTAINER_ID, a);

    if (
      range === undefined ||
      (a.tagName === 'mark' && !getAdditionalCharsMatch(range, htmlDoc, a))
    ) {
      elementsNotFound = [...elementsNotFound, a?.id || ''];
    } else {
      // add icon after each range
      if (a.iconClassName && a.description) {
        const accessibleIconBtn = getAccessibleIconBtn({
          id: a.parentId || '',
          iconClassName: a.iconClassName || '',
          description: a.description || '',
        });
        const clonedRange = range.cloneRange();

        clonedRange.collapse();
        clonedRange.insertNode(accessibleIconBtn);
      }
      // Enclose every text node in range with a <mark>/<a>-tag
      surroundTextNodesInRange({
        contentArea: a,
        range,
      });
    }
  });

  // Parse altered html content back to string
  const serializer = new XMLSerializer();
  const htmlFragment = new DocumentFragment();
  const rootEl = htmlDoc.getElementById(CALCULATION_CONTENT_CONTAINER_ID);
  let newContent = '';

  if (rootEl) {
    htmlFragment.append(rootEl);
  }

  if (htmlFragment) {
    newContent = serializer
      .serializeToString(htmlFragment)
      .replaceAll(/(?:^<div(.*?)>)|(?:<\/div>$)/g, '');
  }

  return { content: newContent, elementsNotFound };
};

export default makeContent;
